import { Link, useHistory, useLocation } from "react-router-dom";
import { DownloadServiceAreaButton } from "@app/region-sets/editor/DownloadServiceAreaButton";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { FC, useEffect, useRef } from "react";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";

type WizardStepProps = {
  actionButton?: React.ReactNode | (() => React.ReactNode);
  baseUrl: string;
  children?: React.ReactNode;
  heading?: string;
  nextUrl?: string;
  prevUrl?: string;
  progressList: string[];
  submitUrl?: string;
  subheading?: string;
  isNextDisabled?: boolean;
  isPrevDisabled?: boolean;
  onPrevClick?: () => void;
  onNextClick?: () => void;
  onSubmitClick?: (runNow: boolean) => void;
  onWizardStepsNavigation?: () => void;
  submitted?: boolean;
};

const submitOptions = [
  {
    key: "true",
    name: "Run Now",
  },
  {
    key: "false",
    name: "Run Later",
  },
];

const WizardStep: FC<WizardStepProps> = (props: WizardStepProps) => {
  const stepBodyRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    focusFirstInteractiveElement(stepBodyRef);
  }, []);

  const handlePrev = (): void => {
    if (props.onPrevClick) {
      props.onPrevClick();
    }

    history.push(`${props.baseUrl}` + "/" + `${props.prevUrl}`);
  }

  const handleNext = (): void => {
    if (props.onNextClick) {
      props.onNextClick();
    }
    history.push(`${props.baseUrl}` + "/" + `${props.nextUrl}`);
  }

  const handleStepChange = (newStep: number): void => {
    if (props.onWizardStepsNavigation) {
      props.onWizardStepsNavigation();
    }
    // newStep will be 0-based but the page numbers are 1-based, so add 1
    history.push(`${props.baseUrl}` + "/" + `${newStep + 1}`);
  }

  const handleFormSubmit = async (runNow: boolean): Promise<void> => {
    if (props.onSubmitClick) {
      props.onSubmitClick(runNow);
    }
  }


  const renderPrev = (props.prevUrl || props.onPrevClick) ? <QESButton qesTestId="wizard-step_previous" qesType="secondary" disabled={props.isPrevDisabled} onClick={handlePrev}>Previous</QESButton> : "";
  const renderNext = (props.nextUrl || props.onNextClick)
    ? <QESButton qesTestId="wizard-step_next" qesType="primary" onClick={handleNext} disabled={props.isNextDisabled}>Next</QESButton>
    : (
      <div className="pull-right">
        <NewActionMenu
          alignRight={true}
          buttonText="Submit"
          testId="wizard-step__submit-dropdown"
          buttonClassNames="qes-button-primary"
          dropup={true}
          loading={props.isNextDisabled}
        >
          {submitOptions.map((option) => {
            return (
              <NewActionMenuItem
                display={option.name}
                isSelected={false}
                key={option.key}
                onClick={() => handleFormSubmit(option.key === "true")}
                testId={`wizard-step__submit-option--${option.key}`}
              />
            );
          })}
        </NewActionMenu>
      </div>
    );
  const renderActionButton = props.actionButton
    ? typeof (props.actionButton) === "function"
      ? (props.actionButton as () => React.ReactNode)()
      : props.actionButton
    : null;

  // +1 because 0-indexed array vs 1-indexed url
  const serviceAreaPageNumber = props.progressList.findIndex((i) => i === "Service Area") + 1;
  const downloadServiceAreaButton = parseInt(location.pathname.split("/").pop(), 10) === serviceAreaPageNumber ?
    (<div className="ms-4 d-inline-block">
      <DownloadServiceAreaButton />
    </div>) :
    null;

  const getCurrentStep = () => {
    /* The prevUrl/page url is 1-based but the wizard steps are 0-based.
     * That means we can use the prevUrl to signify the current page.
     */
    return (props.prevUrl ? +props.prevUrl : 0);
  }

  return (
    <Page documentTitle="Create New Project">
      <PageInner>
        <header className="page-title-bar row align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">{props.heading}</h1>
        </header>
        <WizardSteps currentStep={getCurrentStep()} steps={props.progressList} setStep={handleStepChange} />
        <hr />
        <PageSection>
          <div className="row">
            <div className="col-12">
              <p className="lead">
                {props.subheading}
              </p>
            </div>
          </div>
          <div className="row" ref={stepBodyRef}>
            <div className="col-12 form-horizontal">
              {props.children}
            </div>
          </div>
        </PageSection>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <div className="col-6">
            <div className="col-6 ps-0 d-inline-block">
              <Link className="qes-button qes-button-secondary danger" type="button" to="/" >Cancel</Link>
            </div>
            {downloadServiceAreaButton}
          </div>
        )}
        rightSide={(
          <div>
            {renderActionButton}
            {renderPrev}
            {renderNext}
          </div>
        )}
      />
    </Page>
  );
}

export default WizardStep;
